import { PageStateData } from "org/pageState/models";
import {
    MetadataColumnExtractorId,
    MetadataColumnWithId,
} from "org/risks/content/types";

import { Codecs, PageFilterAndColumns } from "../../types";
import { unwrapPageStateAs } from "../../utils";

import {
    computeIdToNameMaps,
    decodeRiskMapColumn,
    decodeRiskMapPageFilters,
    encodeRiskMapColumn,
    encodeRiskMapFilters,
} from "./common";

function createDecodeRiskDirectoryPageStateFn(
    customColumns: MetadataColumnWithId<MetadataColumnExtractorId>[]
): (pageState: RiskDirectoryPageStateWithType) => RiskDirPageStateRaw {
    const [mapIdToName, mapOptionIdToName] = computeIdToNameMaps(customColumns);
    return (pageState: RiskDirectoryPageStateWithType): RiskDirPageStateRaw => {
        const unwrapped = unwrapPageStateAs(pageState, "RiskDirectory");
        const {
            filtersAndColumns: { filters, columns },
            showTableView,
        } = unwrapped;
        const filter = decodeRiskMapPageFilters(
            mapIdToName,
            mapOptionIdToName,
            filters,
            "RiskDirectory"
        );
        const visibleColumnIds = new Set(
            columns.map((col) => decodeRiskMapColumn(mapIdToName, col))
        );
        return {
            pageType: pageState.type,
            filter,
            visibleColumnIds,
            showTableView,
            version: pageState.filtersAndColumns.version ?? 0,
        };
    };
}

function encodeRiskDirectoryPageState(
    raw: RiskDirPageStateRaw
): RiskDirectoryPageStateWithType {
    const { filter, visibleColumnIds, showTableView } = raw;
    return {
        type: "RiskDirectory",
        filtersAndColumns: {
            filters: encodeRiskMapFilters("RiskDirectory", filter),
            columns: Array.from(visibleColumnIds).map(encodeRiskMapColumn),
            version: raw.version,
        },
        showTableView,
    };
}

export interface RiskDirPageStateRaw extends PageFilterAndColumns {
    showTableView: boolean;
}

export type RiskDirectoryPageStateWithType = PageStateData<"RiskDirectory"> & {
    type: "RiskDirectory";
};

export function pageStateRiskDirectoryCodecs(
    customColumns: MetadataColumnWithId<MetadataColumnExtractorId>[]
): Codecs<RiskDirPageStateRaw, RiskDirectoryPageStateWithType> {
    return {
        encode: encodeRiskDirectoryPageState,
        decode: createDecodeRiskDirectoryPageStateFn(customColumns),
    };
}
